'use client';

import {useLogCaptchaValidationResult} from '@shared/Captcha/Client/useLogCaptchaValidationResult';
import {FormContentOrResultView} from '@shared/SupportModal/FormContentOrResultView/FormContentOrResultView';
import {HelpForm} from '@shared/SupportModal/HelpForm/HelpForm';
import Image from 'next/image';
import {useCallback, useState} from 'react';

import Close from '@shared/SupportModal/assets/close.svg';

import {
  FormSubmissionState,
  SuccessScreenContentType,
} from '@shared/SupportModal/FormContentOrResultView/types';
import {
  HelpFormRequestType,
  WriteSupportForm,
} from '@shared/SupportModal/types';

import {useLocale} from '@utils/hooks/useLocale';
import {usePreventPageScrollOnMount} from '@utils/hooks/usePreventPageScrollOnMount';

import {acceptStrapiForm} from '@strapi/utils/acceptStrapiForm';

import styles from './SupportModal.module.scss';

type Props = {
  setShowModal: () => void;
  writeSupportForm: WriteSupportForm;
  successScreenContent: SuccessScreenContentType;
  submitApiUrl: string;
  fileUploadApiUrl: string;
};

export const SupportModal = ({
  setShowModal,
  successScreenContent,
  writeSupportForm,
  submitApiUrl,
  fileUploadApiUrl,
}: Props) => {
  const [formState, setFormState] = useState<FormSubmissionState>('initial');
  const locale = useLocale();
  const logCaptchaValidationResult = useLogCaptchaValidationResult();

  const acceptForm = (data: HelpFormRequestType) => {
    acceptStrapiForm(submitApiUrl, data, 'dev-landing-supports', locale).then(
      ({result, errorDetail}) => {
        setFormState(result);
        logCaptchaValidationResult(data.captchaResult, {
          error: result !== 'success',
          errorDetail,
        });
      },
    );
  };

  usePreventPageScrollOnMount();

  const onError = useCallback(() => setFormState('error'), []);
  return (
    <div className={styles.root} data-testid={'support_modal'}>
      <div className={styles.header}>
        <Image
          src={Close.src}
          onClick={setShowModal}
          data-testid={'close_button_id'}
          alt={'Hide button'}
          width={36}
          height={36}
        />
      </div>
      <FormContentOrResultView
        formState={formState}
        successMessages={successScreenContent}
      >
        <div className={styles.container}>
          <div className={styles.title}>{writeSupportForm.title}</div>
          <HelpForm
            formData={writeSupportForm}
            acceptForm={acceptForm}
            fileUploadApiUrl={fileUploadApiUrl}
            onError={onError}
          />
        </div>
      </FormContentOrResultView>
    </div>
  );
};
